import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Grid,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import toast from 'react-hot-toast';

const AllAdmin = ({ schoolId }) => {
    const [admins, setAdmins] = useState([]);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        fetchAdmins();
    }, [schoolId]);

    const fetchAdmins = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/v1/getAllAdmins?schoolId=${schoolId}`);
            const data = await response.json();
            setAdmins(data.admins);
        } catch (error) {
            console.error('Error fetching admins:', error);
        }
    };

    const handleEditClick = (admin) => {
        setEditingAdmin(admin);
        setOpenEditDialog(true);
    };

    const handleEditDialogClose = () => {
        setEditingAdmin(null);
        setOpenEditDialog(false);
    };

    const handleDeleteClick = (admin) => {
        setEditingAdmin(admin);
        setOpenDeleteDialog(true);
    };

    const handleDeleteDialogClose = () => {
        setEditingAdmin(null);
        setOpenDeleteDialog(false);
    };

    const handleUpdateAdmin = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/v1/updateAdmin/${editingAdmin._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editingAdmin),
            });
            if (response.ok) {
                console.log('Admin updated:', editingAdmin);
                setOpenEditDialog(false);
                setEditingAdmin(null);
                fetchAdmins(); // Refetch admins after update
                toast.success('Admin information updated successfully');
            } else {
                console.error('Failed to update admin:', response.statusText);
                toast.error('Failed to update admin');
            }
        } catch (error) {
            console.error('Error updating admin:', error);
            toast.error('Failed to update admin');
        }
    };

    const handleDeleteAdmin = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/v1/deleteAdminById/${editingAdmin._id}`, {
                method: 'PUT',
            });
            if (response.ok) {
                console.log('Admin deleted:', editingAdmin);
                setOpenDeleteDialog(false);
                setEditingAdmin(null);
                fetchAdmins(); // Refetch admins after delete
                toast.success('Admin deleted successfully');
            } else {
                console.error('Failed to delete admin:', response.statusText);
                toast.error('Failed to delete admin');
            }
        } catch (error) {
            console.error('Error deleting admin:', error);
            toast.error('Failed to delete admin');
        }
    };

    return (
        <div style={{ padding: 20 }}>
            <Typography variant="h6" gutterBottom>
                Admin Information
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Full Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Address</TableCell>
                                    <TableCell>Aadhaar Number</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {admins.map((admin) => (
                                    <TableRow key={admin._id}>
                                        <TableCell>{admin.fullName}</TableCell>
                                        <TableCell>{admin.email}</TableCell>
                                        <TableCell>{admin.address}</TableCell>
                                        <TableCell>{admin.aadhaarNumber}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={() => handleEditClick(admin)}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                color="secondary"
                                                onClick={() => handleDeleteClick(admin)}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={handleEditDialogClose}>
                <DialogTitle>Edit Admin</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="fullName"
                        label="Full Name"
                        type="text"
                        fullWidth
                        value={editingAdmin?.fullName}
                        onChange={(e) =>
                            setEditingAdmin({
                                ...editingAdmin,
                                fullName: e.target.value,
                            })
                        }
                    />
                    <TextField
                        margin="dense"
                        id="email"
                        label="Email"
                        type="email"
                        fullWidth
                        value={editingAdmin?.email}
                        onChange={(e) =>
                            setEditingAdmin({
                                ...editingAdmin,
                                email: e.target.value,
                            })
                        }
                    />
                    <TextField
                        margin="dense"
                        id="address"
                        label="Address"
                        type="text"
                        fullWidth
                        value={editingAdmin?.address}
                        onChange={(e) =>
                            setEditingAdmin({
                                ...editingAdmin,
                                address: e.target.value,
                            })
                        }
                    />

                    <TextField
                        margin="dense"
                        id="aadhaarNumber"
                        label="Aadhaar Number"
                        type="text"
                        fullWidth
                        value={editingAdmin?.aadhaarNumber}
                        onChange={(e) =>
                            setEditingAdmin({
                                ...editingAdmin,
                                aadhaarNumber: e.target.value,
                            })
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleUpdateAdmin} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleDeleteDialogClose}>
                <DialogTitle>Delete Admin</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to delete this admin?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteAdmin} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AllAdmin;
